import { NEXT_PUBLIC_NO_PRICE_FALLBACK } from '@config/environment';
import { FREE_SHIPPING_MIN_AMOUNT, DELIVERY_COST } from '@config/constants';
import {
  RootDiv,
  TitleContainer,
  TopContainer,
  TitleTypography,
  PriceContainer,
  PriceTypography,
  MessageTypography,
  FreeDeliveryProgressSlider,
  LocalShippingIcon,
} from './FreeDeliveryProgressCard.styles';
import { Skeleton } from '@mui/material';
import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';
import { useAuth } from '@hooks/useAuth';
import {
  parseISO,
  differenceInHours,
  differenceInMinutes,
  format,
} from 'date-fns';
import { es } from 'date-fns/locale';

const currFormat = new CurrencyFormatter({
  fallback: 'Gratis',
});

interface GetDeliveryMessageProps {
  subTotalCart: number;
  hasDeliveryCoupons: boolean;
  expirationDate: Date | null;
}

const GetDeliveryMessage = ({
  subTotalCart,
  hasDeliveryCoupons,
  expirationDate,
}: GetDeliveryMessageProps) => {
  if (hasDeliveryCoupons && expirationDate) {
    const now = new Date();
    const hoursLeft = differenceInHours(expirationDate, now);
    const minutesLeft = differenceInMinutes(expirationDate, now);

    if (hoursLeft >= 24) {
      return (
        <MessageTypography>
          Hasta antes del{' '}
          <span>
            {format(expirationDate, "d 'de' MMMM 'a las' h:mm a", {
              locale: es,
            })}
          </span>{' '}
          el despacho es <span>GRATIS</span>.
        </MessageTypography>
      );
    } else if (hoursLeft >= 2) {
      return (
        <MessageTypography>
          Dentro de las <span>próximas {hoursLeft} horas</span> el despacho es{' '}
          <span>GRATIS</span>.
        </MessageTypography>
      );
    } else if (hoursLeft >= 1) {
      return (
        <MessageTypography>
          Dentro de la <span>próxima hora</span> el despacho es{' '}
          <span>GRATIS</span>.
        </MessageTypography>
      );
    } else {
      return (
        <MessageTypography>
          Dentro de los <span>próximos {minutesLeft} minutos</span> el despacho
          es <span>GRATIS</span>.
        </MessageTypography>
      );
    }
  }

  if (subTotalCart < FREE_SHIPPING_MIN_AMOUNT) {
    return (
      <MessageTypography>
        Agrega{' '}
        <span>
          {currFormat.format(FREE_SHIPPING_MIN_AMOUNT - subTotalCart)}
        </span>{' '}
        para tener <span>envío gratis</span>
      </MessageTypography>
    );
  }

  return (
    <MessageTypography>
      ¡Aprovecha y <span>agrega más productos!</span>
    </MessageTypography>
  );
};

interface FreeDeliveryProgressCardProps {
  subTotalCart: number;
  loading: boolean;
}
export const FreeDeliveryProgressCard = ({
  subTotalCart,
  loading,
}: FreeDeliveryProgressCardProps) => {
  const { appUser } = useAuth();

  const hasDeliveryCoupons = appUser?.deliveryCoupons?.count > 0;
  const expirationDate = hasDeliveryCoupons
    ? parseISO(appUser?.deliveryCoupons?.minExpirationDate)
    : null;

  const deliveryCost =
    hasDeliveryCoupons || subTotalCart >= FREE_SHIPPING_MIN_AMOUNT
      ? 0
      : DELIVERY_COST;
  const deliveryProgress = hasDeliveryCoupons
    ? 100
    : (subTotalCart / FREE_SHIPPING_MIN_AMOUNT) * 100;

  return (
    <RootDiv>
      {loading ? (
        <Skeleton height="58px" width="100%" variant="rectangular" />
      ) : (
        <>
          <TopContainer>
            <TitleContainer>
              <LocalShippingIcon sx={{ fontSize: '18px' }} />
              <TitleTypography>Despacho</TitleTypography>
            </TitleContainer>
            <PriceContainer>
              <PriceTypography>
                {currFormat.format(deliveryCost)}
              </PriceTypography>
            </PriceContainer>
          </TopContainer>
          <FreeDeliveryProgressSlider
            value={deliveryProgress}
            size="small"
            disabled
            aria-label="Progreso para despacho gratis"
          />
          <GetDeliveryMessage
            subTotalCart={subTotalCart}
            hasDeliveryCoupons={hasDeliveryCoupons}
            expirationDate={expirationDate}
          />
        </>
      )}
    </RootDiv>
  );
};
